import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Typography, Box, FormHelperText, FormControl, IconButton, Link } from "@material-ui/core";
import pick from "lodash/pick";
import { useFormik } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useParams } from "react-router-dom";
import { Input } from "components/Shared/mui-formik-inputs";
import { SupplierGroupSchema } from "constants/validation-schemas";
import { PATHS } from "util/appConstants";
import FormCancelSaveButton from "components/Shared/FormCancelSaveButtons";
import DeleteIcon from "@material-ui/icons/Delete";

const useStyles = makeStyles({
  _heading: {
    font: "normal normal normal 28px/40px Questrial",
    color: "#121212",
  },
  _icons: {
    color: "#ADADAD",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    margin: "0 16px 0 0px",
    fontSize: "35px",
  },
  _save: {
    "&:hover": {
      transform: "scale(1.3)",
      color: "#6F9CEB",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _close: {
    "&:hover": {
      transform: "scale(1.3)",
      color: "#525252",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _subheading: {
    font: "normal normal 500 22px/32px Roboto",
    color: " #121212",
    marginTop: "44px",
    marginBottom: "40px",
  },
  _dflex: {
    display: "flex",
    alignItems: "center",
  },
});

const SupplierGroupForm = ({ initialValues, onSubmit, action }) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const [selectedFile, setSelectedFile] = useState(null);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema: SupplierGroupSchema,
    initialValues: {
      name: "", // required
      ...initialValues,
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const formData = new FormData();
        const filteredValues = pick(values, ["name"]);
        Object.keys(filteredValues).forEach((key) => {
          formData.append(key, filteredValues[key]);
        });
        if (action === "ADD") {
          formData.append("filename", selectedFile);
        } else if (action === "EDIT") {
          if (selectedFile) {
            formData.append("keepExistingFile", "true");
            formData.append("filename", selectedFile);
          } else if (values.filename === initialValues.filename) {
            formData.append("keepExistingFile", "true");
            formData.append("filename", initialValues.filename);
          } else {
            formData.append("keepExistingFile", "false");
            formData.append("filename", null);
          }
        }
        await onSubmit(formData);
      } catch (err) {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    handleChange,
    setFieldValue,
    errors,
    handleSubmit,
    submitCount,
    isValid,
    isSubmitting,
    setFieldTouched,
  } = formik;
  let { handleBlur } = formik;

  if (!submitCount) {
    handleBlur = null;
  }

  const closeCustomerHandler = () => {
    action === "ADD"
      ? history.push(PATHS.admin.supplierGroups)
      : history.push(PATHS.admin.supplierGroupsDetail.replace(":id", id));
  };

  const cancelButtonRef = useRef(null);
  const saveButtonRef = useRef(null);


  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    if (file) {
      setFieldValue("filename", file);
      setFieldTouched("filename", true, false);
      setTimeout(() => {
        formik.validateField("filename");
      }, 100);
    }
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
    setFieldValue("filename", null);
  };

  return (
    <Box height="100%">
      <Box display="flex" mb={4}>
        <Box flex={2}>
          <Typography className={classes._heading} variant="h4">
            {action === "ADD" ? t("New Account") : t("Edit Account")}
          </Typography>
        </Box>
        <Box flex={2} textAlign="right" position="relative">
          <Box position="absolute" right={0}>
            <FormCancelSaveButton
              disabled={!isValid || isSubmitting}
              onCancel={closeCustomerHandler}
              onSave={handleSubmit}
              ref={{ cancelRef: cancelButtonRef, saveRef: saveButtonRef }}
            />
          </Box>
        </Box>
      </Box>
      <Typography className={classes._subheading} variant="h5">
        {t("Basic Data")}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Input
            label={t("Name")}
            name="name"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.name}
            errors={errors}
            required
          />
        </Grid>

      </Grid>
      <>
        <Typography className={classes._subheading} variant="h5">
          {t("Logo")}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <FormControl fullWidth error={Boolean(errors.filename)} style={{ marginTop: "20px" }}>
              <Box display="flex" alignItems="center">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    padding: "16px",
                    border: "1px dashed #c4c4c4",
                    borderRadius: "8px",
                    cursor: "pointer",
                    textAlign: "center",
                    marginRight: "10px",
                  }}
                >
                  <input
                    type="file"
                    id="file-input"
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: "none" }}
                  />
                  <label htmlFor="file-input" style={{ cursor: "pointer", color: "#000" }}>
                    <Typography variant="body1" color="textSecondary">
                      {t("Upload Logo")}
                    </Typography>
                  </label>
                </Box>

                {/* Display the filename link if available */}
                {(selectedFile || values?.filename) && (
                  <Box display="flex" alignItems="center" maxWidth="150px" overflow="hidden">
                    <Link
                      href={selectedFile ? URL.createObjectURL(selectedFile) : values.image_url}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        color: "black",
                        marginRight: "10px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {selectedFile ? selectedFile.name : values.filename}
                    </Link>
                    <IconButton size="small" onClick={handleFileRemove} aria-label="delete file">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>
              {errors.filename && <FormHelperText>{t(errors.filename)}</FormHelperText>}
            </FormControl>
          </Grid>
        </Grid>
      </>
    </Box>
  );
};
export default SupplierGroupForm;
